import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { daysBetween, fmtN } from "../../lib/funnel/helpers";
import { PRIMARY, MUTED, FG1, FG3, GOLD } from "../../lib/funnel/theme";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";
import type { BaselineRow } from "./sql";
import { HEATMAP_FEATURES, HEATMAP_WEEKS } from "./helpers";
import type { HeatmapFeature } from "./helpers";

// 2-D heatmap: rows = features (expandable to deeper thresholds), columns =
// cumulative week buckets since signup. Cell = % of cohort that hit the
// feature within that window. Column denominator shrinks past week 4 because
// the cohort's minimum observation window is 30 days.

type ViewMode = "pct" | "abs";
type VizMode = "heat" | "bar";

// Hex string -> [r,g,b] triple for the heat-fill calculation.
function hexToRgb(hex: string): [number, number, number] {
  const m = hex.replace("#", "").match(/.{2}/g) ?? ["2F", "7A", "8A"];
  return [parseInt(m[0] ?? "00", 16), parseInt(m[1] ?? "00", 16), parseInt(m[2] ?? "00", 16)];
}
const PRIMARY_RGB: [number, number, number] = hexToRgb(PRIMARY);
const GOLD_RGB: [number, number, number] = hexToRgb(GOLD);

// Neutral charcoal used for the child-row palette so the parent row carries
// the teal accent and children read as secondary evidence.
const GREY_RGB: [number, number, number] = [90, 90, 90];

type CellPalette = "primary" | "gold" | "grey";
function cellFill(intensity: number, palette: CellPalette): string {
  const rgb = palette === "grey" ? GREY_RGB : palette === "gold" ? GOLD_RGB : PRIMARY_RGB;
  const [r, g, b] = rgb;
  const alpha = palette === "grey"
    ? 0.04 + intensity * 0.32
    : 0.08 + intensity * 0.78;
  return `rgba(${r},${g},${b},${alpha})`;
}

type RowDatum = {
  feature: HeatmapFeature;
  depth: number;
  byWeek: number[];      // numerator per week
  ever: number;          // numerator across full observation
  everDenom: number;     // denominator for the "ever" column = full cohort size
};

function compute(rows: BaselineRow[], feature: HeatmapFeature): RowDatum {
  const byWeek = HEATMAP_WEEKS.map(() => 0);
  let ever = 0;
  for (const u of rows) {
    const ev = feature.eventDate(u);
    if (!ev) continue;
    ever += 1;
    const d = daysBetween(u.signup_date_utc, ev);
    for (let i = 0; i < HEATMAP_WEEKS.length; i++) {
      const wb = HEATMAP_WEEKS[i];
      if (!wb) continue;
      if (d <= wb.days) byWeek[i] = (byWeek[i] ?? 0) + 1;
    }
  }
  return { feature, depth: 0, byWeek, ever, everDenom: rows.length };
}

export function FeatureWeekHeatmap({
  users,
  pagePills,
  compareUsers,
  comparePills,
}: {
  users: BaselineRow[];
  pagePills?: FilterPillSpec[];
  compareUsers?: BaselineRow[] | null;
  comparePills?: FilterPillSpec[];
}) {
  const compareOn = !!compareUsers;
  const [view, setView] = useState<ViewMode>("pct");
  const [viz, setViz] = useState<VizMode>("heat");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [hiddenRows, setHiddenRows] = useState<Set<string>>(new Set());
  const [rowsMenuOpen, setRowsMenuOpen] = useState(false);
  const rowsMenuRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLElement | null>(null);
  const [tooltip, setTooltip] = useState<{
    x: number; y: number; week: string; feature: string;
    count: number; denom: number; pct: number;
    compareCount?: number; compareDenom?: number; comparePct?: number;
    rowMode?: "dual" | "baseline" | "comparison";
  } | null>(null);

  useEffect(() => {
    if (!rowsMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (rowsMenuRef.current && !rowsMenuRef.current.contains(e.target as Node)) setRowsMenuOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [rowsMenuOpen]);

  const visibleFeatures = useMemo(
    () => HEATMAP_FEATURES.filter((f) => !hiddenRows.has(f.key)),
    [hiddenRows],
  );

  // Single cohort-wide denominator across every column. Using a fixed denom
  // keeps the reach numbers monotonic — a user who has hit the feature stays
  // counted forever, so a column can never go down as the window widens.
  const cohortDenom = users.length;
  const compareDenom = compareUsers?.length ?? 0;

  const data = useMemo(() => visibleFeatures.map((f) => compute(users, f)), [users, visibleFeatures]);
  const compareData = useMemo(
    () => compareUsers ? visibleFeatures.map((f) => compute(compareUsers, f)) : null,
    [compareUsers, visibleFeatures],
  );
  const childData = useMemo(() => {
    const out: Record<string, RowDatum[]> = {};
    for (const parent of visibleFeatures) {
      if (!parent.children) continue;
      out[parent.key] = parent.children.map((c) => compute(users, c));
    }
    return out;
  }, [users, visibleFeatures]);
  const compareChildData = useMemo(() => {
    if (!compareUsers) return null;
    const out: Record<string, RowDatum[]> = {};
    for (const parent of visibleFeatures) {
      if (!parent.children) continue;
      out[parent.key] = parent.children.map((c) => compute(compareUsers, c));
    }
    return out;
  }, [compareUsers, visibleFeatures]);

  // Single intensity scale anchored to the highest % of cohort across every
  // cell in the heatmap (rows × weeks + Ever). Same ramp everywhere so cells
  // are comparable across the whole table, not just within a column.
  const globalMaxPct = useMemo(() => {
    let max = 0;
    const consider = (n: number, denom: number) => {
      const pct = denom > 0 ? n / denom : 0;
      if (pct > max) max = pct;
    };
    for (const row of data) {
      for (let i = 0; i < HEATMAP_WEEKS.length; i++) consider(row.byWeek[i] ?? 0, cohortDenom);
      consider(row.ever, row.everDenom);
    }
    if (compareData) {
      for (const row of compareData) {
        for (let i = 0; i < HEATMAP_WEEKS.length; i++) consider(row.byWeek[i] ?? 0, compareDenom);
        consider(row.ever, compareDenom);
      }
    }
    return max;
  }, [data, compareData, cohortDenom, compareDenom]);

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function renderSubCell(numerator: number, denom: number, max: number, palette: CellPalette): React.ReactNode {
    if (denom === 0) {
      return <span style={{ color: MUTED, fontSize: 12 }}>-</span>;
    }
    const pct = numerator / denom;
    const intensity = max > 0 ? pct / max : 0;
    const display = view === "pct" ? `${(pct * 100).toFixed(0)}%` : fmtN(numerator);
    const rgb = palette === "grey" ? GREY_RGB : palette === "gold" ? GOLD_RGB : PRIMARY_RGB;
    const [r, g, b] = rgb;
    if (viz === "heat") {
      return (
        <div style={{
          background: cellFill(intensity, palette),
          borderRadius: 6,
          padding: "5px 6px",
          color: palette === "grey" ? FG1 : (intensity < 0.5 ? FG1 : "#fff"),
          fontWeight: 600,
          fontSize: 12,
        }}>
          {display}
        </div>
      );
    }
    return (
      <div style={{ position: "relative", width: "100%", height: 26, borderRadius: 5, overflow: "hidden", background: "var(--bg-2)" }}>
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: `${pct * 100}%`,
          background: palette === "grey" ? `rgba(${r},${g},${b},0.35)` : `rgba(${r},${g},${b},0.65)`,
          borderRadius: 5,
        }} />
        <span style={{
          position: "absolute", left: 0, right: 0, top: 0, bottom: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, fontWeight: 600,
          color: palette === "grey" ? FG1 : (pct > 0.55 ? "#fff" : FG1),
        }}>
          {display}
        </span>
      </div>
    );
  }

  type RowMode = "dual" | "baseline" | "comparison";

  function renderCell(
    numerator: number,
    denom: number,
    max: number,
    indented: boolean,
    weekLabel: string,
    featureLabel: string,
    cellKey: string,
    compareNumerator?: number,
    compareDenomLocal?: number,
    rowMode: RowMode = "dual",
  ) {
    const pct = denom > 0 ? numerator / denom : 0;
    const cpct = compareDenomLocal && compareDenomLocal > 0
      ? (compareNumerator ?? 0) / compareDenomLocal
      : 0;
    const palette: CellPalette = indented
      ? "grey"
      : rowMode === "comparison" ? "gold" : "primary";
    return (
      <td
        key={cellKey}
        style={{ padding: "3px 4px", textAlign: "center", opacity: indented ? 0.95 : 1 }}
        onMouseMove={(e) => {
          const rect = cardRef.current?.getBoundingClientRect();
          if (!rect) return;
          setTooltip({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            week: weekLabel,
            feature: featureLabel,
            count: numerator,
            denom,
            pct,
            compareCount: rowMode === "dual" && compareOn ? compareNumerator : undefined,
            compareDenom: rowMode === "dual" && compareOn ? compareDenomLocal : undefined,
            comparePct: rowMode === "dual" && compareOn ? cpct : undefined,
            rowMode,
          });
        }}
        onMouseLeave={() => setTooltip(null)}
      >
        {rowMode === "dual" && compareOn && compareNumerator !== undefined && compareDenomLocal !== undefined ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {renderSubCell(numerator, denom, max, palette)}
            {renderSubCell(compareNumerator, compareDenomLocal, max, indented ? "grey" : "gold")}
          </div>
        ) : (
          renderSubCell(numerator, denom, max, palette)
        )}
      </td>
    );
  }

  function renderRow(
    row: RowDatum,
    depth = 0,
    parentKey?: string,
    isFirst = false,
    isLastChild = false,
    compareRow?: RowDatum,
    rowMode: RowMode = "dual",
  ) {
    const hasChildren = (row.feature.children?.length ?? 0) > 0 && depth === 0;
    const isExpanded = expanded.has(row.feature.key);
    const totalCols = HEATMAP_WEEKS.length + 2;
    const accentColor = rowMode === "comparison" ? GOLD : PRIMARY;

    // When a parent is expanded with comparison on, split the section into
    // a baseline parent + its baseline children, then a comparison parent +
    // its comparison children — instead of the collapsed dual-stacked row.
    const splitExpanded = depth === 0 && isExpanded && compareOn && rowMode === "dual";
    if (splitExpanded && compareRow) {
      return (
        <Fragment key={(parentKey ?? "") + row.feature.key}>
          {!isFirst && (
            <tr aria-hidden="true">
              <td colSpan={totalCols} style={{ padding: 0, height: 10, background: "transparent" }} />
            </tr>
          )}
          {renderRow(row, 0, parentKey + ":b", true, false, undefined, "baseline")}
          {childData[row.feature.key]?.map((c, ci, arr) =>
            renderRow(c, 1, row.feature.key + ":b", false, ci === arr.length - 1, undefined, "baseline"),
          )}
          <tr aria-hidden="true">
            <td colSpan={totalCols} style={{ padding: 0, height: 6, background: "transparent" }} />
          </tr>
          {renderRow(compareRow, 0, parentKey + ":c", true, false, undefined, "comparison")}
          {compareChildData?.[row.feature.key]?.map((c, ci, arr) =>
            renderRow(c, 1, row.feature.key + ":c", false, ci === arr.length - 1, undefined, "comparison"),
          )}
        </Fragment>
      );
    }

    // Each parent + its (expanded) children form a "section" that gets a
    // dashed border drawn around it via cell-level borders. Collapsed
    // parents are single-row sections.
    const anyExpanded = expanded.size > 0;
    return (
      <Fragment key={(parentKey ?? "") + row.feature.key}>
        {depth === 0 && !isFirst && anyExpanded && (
          <tr aria-hidden="true">
            <td colSpan={totalCols} style={{ padding: 0, height: 10, background: "transparent" }} />
          </tr>
        )}
        <tr style={{ background: depth === 0 ? "var(--bg-2, #f5f4f2)" : "var(--bg-1)" }}>
          <td
            onClick={hasChildren ? () => toggle(row.feature.key) : undefined}
            style={{
              padding: depth === 0 ? "3px 8px 3px 8px" : "3px 8px 3px 28px",
              color: depth === 0 ? "var(--fg-1)" : FG3,
              fontWeight: depth === 0 ? 700 : 500,
              fontSize: depth === 0 ? 13 : 12,
              whiteSpace: "nowrap",
              cursor: hasChildren ? "pointer" : "default",
              userSelect: hasChildren ? "none" : "auto",
              borderLeft: rowMode !== "dual" ? `3px solid ${accentColor}` : undefined,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.feature.label}
                {rowMode !== "dual" && (
                  <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: accentColor, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                    {rowMode === "comparison" ? "cmp" : "base"}
                  </span>
                )}
              </span>
              {hasChildren && (
                <i
                  className={`ti ti-chevron-${isExpanded ? "up" : "down"}`}
                  style={{ fontSize: 12, color: FG3, flexShrink: 0 }}
                />
              )}
            </span>
          </td>
          {HEATMAP_WEEKS.map((wb, i) => renderCell(
            row.byWeek[i] ?? 0,
            rowMode === "comparison" ? compareDenom : cohortDenom,
            globalMaxPct,
            depth > 0,
            `end of ${wb.label.toLowerCase()}`,
            row.feature.label.toLowerCase(),
            wb.key,
            compareRow?.byWeek[i] ?? (compareOn ? 0 : undefined),
            compareOn ? compareDenom : undefined,
            rowMode,
          ))}
          {renderCell(
            row.ever,
            rowMode === "comparison" ? compareDenom : row.everDenom,
            globalMaxPct,
            depth > 0,
            "any time so far",
            row.feature.label.toLowerCase(),
            "ever",
            compareRow?.ever ?? (compareOn ? 0 : undefined),
            compareOn ? (compareRow?.everDenom ?? compareDenom) : undefined,
            rowMode,
          )}
        </tr>
        {hasChildren && isExpanded && !compareOn && childData[row.feature.key]?.map((c, ci, arr) =>
          renderRow(
            c, 1, row.feature.key, false, ci === arr.length - 1,
            undefined, "dual",
          ),
        )}
      </Fragment>
    );
  }

  const pills: FilterPillSpec[] = [
    ...(pagePills ?? []),
    ...(compareOn ? [{ kind: "mode" as const, label: "vs comparison" }, ...(comparePills ?? [])] : []),
    { kind: "mode" as const, label: viz === "heat" ? "Heat view" : "Bar view" },
    { kind: "mode" as const, label: view === "pct" ? "% of cohort" : "# users" },
  ];

  return (
    <section className="card" ref={cardRef} style={{ position: "relative" }}>
      <SectionCardHeader
        title="Feature reach by week since sign-up"
        subtitle="Each cell is the cumulative share of the cohort that touched the feature by end of that week. Same denominator across every column, so cells are directly comparable. Expand a row for deeper thresholds; use the Rows menu to hide features that don't matter for your read."
        filterPills={pills}
        headerExtra={
          <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <div ref={rowsMenuRef} style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setRowsMenuOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={rowsMenuOpen}
                className="btn btn-sm"
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <i className="ti ti-list-check" style={{ fontSize: 13 }} />
                Rows
                {hiddenRows.size > 0 && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, color: PRIMARY,
                    background: "rgba(47,122,138,0.12)", padding: "1px 5px", borderRadius: 8,
                  }}>
                    {HEATMAP_FEATURES.length - hiddenRows.size}/{HEATMAP_FEATURES.length}
                  </span>
                )}
                <i className="ti ti-chevron-down" style={{ fontSize: 11, color: FG3 }} />
              </button>
              {rowsMenuOpen && (
                <div
                  role="listbox"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: "absolute", top: "calc(100% + 4px)", right: 0,
                    background: "var(--white)", border: "1px solid var(--border-default)",
                    borderRadius: 6, boxShadow: "var(--shadow-md)", padding: 6, zIndex: 10,
                    minWidth: 220, fontSize: 12,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 6px 6px", borderBottom: "1px solid var(--border-default)" }}>
                    <button
                      type="button"
                      onClick={() => setHiddenRows(new Set())}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: PRIMARY, fontSize: 11, fontWeight: 500 }}
                    >
                      Show all
                    </button>
                    <button
                      type="button"
                      onClick={() => setHiddenRows(new Set(HEATMAP_FEATURES.map((f) => f.key)))}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: FG3, fontSize: 11, fontWeight: 500 }}
                    >
                      Hide all
                    </button>
                  </div>
                  {HEATMAP_FEATURES.map((f) => {
                    const visible = !hiddenRows.has(f.key);
                    return (
                      <label
                        key={f.key}
                        style={{
                          display: "flex", alignItems: "center", gap: 8, padding: "5px 6px",
                          cursor: "pointer", borderRadius: 4,
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setHiddenRows((prev) => {
                            const next = new Set(prev);
                            if (next.has(f.key)) next.delete(f.key); else next.add(f.key);
                            return next;
                          });
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => {}}
                          tabIndex={-1}
                          style={{ pointerEvents: "none" }}
                        />
                        <span style={{ color: visible ? FG1 : FG3 }}>{f.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div role="group" aria-label="Visualisation mode" className="btn-group" style={{ flexShrink: 0 }}>
              <button
                type="button"
                className={`btn btn-sm${viz === "heat" ? " is-active" : ""}`}
                onClick={() => setViz("heat")}
                style={{ fontWeight: viz === "heat" ? 600 : 400 }}
              >
                <i className="ti ti-layout-grid" style={{ marginRight: 4 }} />Heat
              </button>
              <button
                type="button"
                className={`btn btn-sm${viz === "bar" ? " is-active" : ""}`}
                onClick={() => setViz("bar")}
                style={{ fontWeight: viz === "bar" ? 600 : 400 }}
              >
                <i className="ti ti-chart-bar" style={{ marginRight: 4 }} />Bar
              </button>
            </div>
            <div role="group" aria-label="Value mode" style={{
              display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11,
            }}>
              {([
                { key: "pct" as ViewMode, label: "%" },
                { key: "abs" as ViewMode, label: "#" },
              ]).map((opt) => {
                const active = view === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setView(opt.key)}
                    style={{
                      padding: "3px 10px",
                      background: active ? PRIMARY : "transparent",
                      color: active ? "#fff" : FG1,
                      border: "none", cursor: "pointer",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        }
      />
      <div className="card-body" style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed", fontSize: 13 }}>
          <colgroup>
            <col style={{ width: "22%" }} />
            {HEATMAP_WEEKS.map((w) => <col key={w.key} style={{ width: `${78 / (HEATMAP_WEEKS.length + 1)}%` }} />)}
            <col style={{ width: `${78 / (HEATMAP_WEEKS.length + 1)}%` }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{
                textAlign: "left", padding: "10px 8px 10px 8px",
                color: FG3, fontWeight: 600, fontSize: 11,
                letterSpacing: "0.05em", textTransform: "uppercase",
                borderBottom: "1px solid var(--border-subtle, rgba(0,0,0,0.08))",
              }}>
                <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span>Feature</span>
                  {(() => {
                    const expandableKeys = visibleFeatures
                      .filter((f) => (f.children?.length ?? 0) > 0)
                      .map((f) => f.key);
                    const allExpanded = expandableKeys.length > 0 && expandableKeys.every((k) => expanded.has(k));
                    return (
                      <button
                        type="button"
                        onClick={() => setExpanded(allExpanded ? new Set() : new Set(expandableKeys))}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          color: PRIMARY,
                          fontSize: 10,
                          fontWeight: 500,
                          textDecoration: "underline",
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        {allExpanded ? "Collapse all" : "Expand all"}
                      </button>
                    );
                  })()}
                </span>
              </th>
              {HEATMAP_WEEKS.map((w) => (
                <th key={w.key} style={{
                  textAlign: "center", padding: "10px 8px",
                  color: FG3, fontWeight: 600, fontSize: 11,
                  letterSpacing: "0.05em", textTransform: "uppercase",
                  borderBottom: "1px solid var(--border-subtle, rgba(0,0,0,0.08))",
                }}>
                  {w.label}
                </th>
              ))}
              <th style={{
                textAlign: "center", padding: "10px 8px",
                color: FG3, fontWeight: 600, fontSize: 11,
                letterSpacing: "0.05em", textTransform: "uppercase",
                borderBottom: "1px solid var(--border-subtle, rgba(0,0,0,0.08))",
              }}>Ever</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => renderRow(row, 0, undefined, i === 0, false, compareData?.[i]))}
          </tbody>
        </table>
      </div>
      {tooltip && (() => {
        const TIP_W = 280;
        const MARGIN = 14;
        const cardW = cardRef.current?.clientWidth ?? 0;
        const flipLeft = tooltip.x + MARGIN + TIP_W > cardW - 8;
        const left = flipLeft ? Math.max(8, tooltip.x - MARGIN - TIP_W) : tooltip.x + MARGIN;
        const verb = tooltip.week === "any time so far" ? "have" : "had";
        const prefix = tooltip.week === "any time so far"
          ? "At any time so far,"
          : `By the ${tooltip.week},`;
        return (
          <div
            style={{
              position: "absolute", left, top: tooltip.y - 10,
              width: TIP_W, background: "var(--white)",
              border: "1px solid var(--border-default)", borderRadius: 7,
              padding: "7px 10px", fontSize: 12, pointerEvents: "none",
              boxShadow: "var(--shadow-md)", color: FG1, zIndex: 20,
              lineHeight: 1.45,
            }}
          >
            <div>
              {prefix}{" "}
              <strong style={{ color: tooltip.rowMode === "comparison" ? GOLD : PRIMARY }}>
                {(tooltip.pct * 100).toFixed(1)}%
              </strong>{" "}
              of the {tooltip.rowMode === "comparison" ? "comparison" : "baseline"} cohort {verb}{" "}
              <strong>{tooltip.feature}</strong>.
            </div>
            <div style={{ color: FG3, marginTop: 3 }}>
              {fmtN(tooltip.count)} of {fmtN(tooltip.denom)} users
            </div>
            {tooltip.comparePct !== undefined && tooltip.compareDenom !== undefined && (
              <>
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${GOLD}66` }}>
                  Comparison:{" "}
                  <strong style={{ color: GOLD }}>{(tooltip.comparePct * 100).toFixed(1)}%</strong>{" "}
                  ({fmtN(tooltip.compareCount ?? 0)} of {fmtN(tooltip.compareDenom)})
                </div>
                <div style={{ color: FG3, marginTop: 2, fontSize: 11 }}>
                  Δ {((tooltip.comparePct - tooltip.pct) * 100 >= 0 ? "+" : "")}{((tooltip.comparePct - tooltip.pct) * 100).toFixed(1)}pp
                </div>
              </>
            )}
          </div>
        );
      })()}
    </section>
  );
}
